import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { showToast } from '../config_and_helpers/helpers';
import { formatLabDataErrors } from './lab_data_client';
import { logError, logInfo } from '../config_and_helpers/logger';
import BudgetCache from '../config_and_helpers/budget_cache';
import { mergeBudgetListRow } from '../utils/budgetListRow';

const LOG_CONTEXT = 'budget_line_client';

/**
 * Institutional Budget CRUD via preferred `/v1/budgets/`
 * (backend alias: `/v1/budget-lines/`).
 *
 * Export names keep `BudgetLine*` for SPA/component compatibility.
 */

/**
 * Parse list payload (array or DRF-paginated `{ results, next }`).
 *
 * @param {*} data
 * @returns {{ results: Array, next: string|null }}
 */
function parseBudgetListPayload(data) {
  if (Array.isArray(data)) {
    return { results: data, next: null };
  }
  if (data && typeof data === 'object') {
    if (Array.isArray(data.results)) {
      return { results: data.results, next: data.next || null };
    }
    if (Array.isArray(data.budgets)) {
      return { results: data.budgets, next: data.next || null };
    }
    if (Array.isArray(data.budget_lines)) {
      return { results: data.budget_lines, next: data.next || null };
    }
  }
  return { results: [], next: null };
}

/**
 * GET /v1/budgets/ (one page). Supports DRF pagination via `next`.
 *
 * @param {string} token
 * @param {{ isActive?: boolean, nextPage?: string|null }} [options]
 * @returns {Promise<{
 *   success: boolean,
 *   data?: Array,
 *   nextPage?: string|null,
 *   reachedEnd?: boolean,
 *   messages?: string[],
 *   status?: number,
 * }>}
 */
export const getBudgetLines = async (token, options = {}) => {
  const { isActive, nextPage = null } = options;

  let url = nextPage
    ? nextPage
    : `${BACKEND_URL}budgets/?page_num=1`;

  if (!nextPage && isActive === true) {
    url += '&is_active=true';
  }

  // Same pattern as getOrders: identical first-page URLs can be HTTP-cached
  // after a PATCH, so the table would keep showing the previous PI.
  const cacheBustTs = !nextPage ? Date.now() : null;
  if (cacheBustTs != null) {
    url += `&_ts=${cacheBustTs}`;
  }

  try {
    logInfo(LOG_CONTEXT, 'getBudgetLines start', {
      isActive,
      path: 'budgets/',
      paginating: Boolean(nextPage),
      cacheBustTs,
    });
    const response = await axios.get(url, {
      headers: { Authorization: `Token ${token}` },
    });
    const { results, next } = parseBudgetListPayload(response.data);
    return {
      success: true,
      data: results,
      nextPage: next || null,
      reachedEnd: !next,
    };
  } catch (error) {
    logError(LOG_CONTEXT, 'getBudgetLines failed', error.response?.data ?? error);
    return {
      success: false,
      messages: formatLabDataErrors(error.response?.data),
      status: error.response?.status,
    };
  }
};

/**
 * Fetch all budget pages (for pickers that need a complete active list).
 *
 * @param {string} token
 * @param {{ isActive?: boolean }} [options]
 */
export const getAllBudgetLines = async (token, options = {}) => {
  const accumulated = [];
  let nextPage = null;
  let isFirst = true;
  let guard = 0;

  logInfo(LOG_CONTEXT, 'getAllBudgetLines start', { isActive: options.isActive });

  while (guard < 100) {
    guard += 1;
    const result = await getBudgetLines(token, {
      isActive: options.isActive,
      nextPage: isFirst ? null : nextPage,
    });
    isFirst = false;
    if (!result.success) {
      return result;
    }
    accumulated.push(...(result.data || []));
    if (result.reachedEnd || !result.nextPage) {
      logInfo(LOG_CONTEXT, 'getAllBudgetLines done', { count: accumulated.length });
      return { success: true, data: accumulated };
    }
    nextPage = result.nextPage;
  }

  logError(LOG_CONTEXT, 'getAllBudgetLines page guard hit', {
    count: accumulated.length,
  });
  return { success: true, data: accumulated };
};

/**
 * Parse DRF `{ results, next }` (or a bare array) for related-order pages.
 *
 * @param {*} data
 * @returns {{ results: Array, next: string|null }}
 */
function parseBudgetOrdersPayload(data) {
  if (Array.isArray(data)) {
    return { results: data, next: null };
  }
  if (data && typeof data === 'object' && Array.isArray(data.results)) {
    return { results: data.results, next: data.next || null };
  }
  return { results: [], next: null };
}

/**
 * Compact related-order stub (`id` + optional `corporate_order_ref`).
 *
 * @param {*} item
 * @returns {{ id: number, corporate_order_ref?: string }|null}
 */
function toRelatedOrderStub(item) {
  if (item == null || item === '') return null;
  if (typeof item === 'number' || typeof item === 'string') {
    const id = Number(item);
    if (!Number.isFinite(id)) return null;
    return { id };
  }
  if (typeof item !== 'object') return null;
  const rawId = item.id;
  if (rawId == null || rawId === '') return null;
  const id = typeof rawId === 'number' ? rawId : Number(rawId);
  if (!Number.isFinite(id)) return null;
  const stub = { id };
  const ref = item.corporate_order_ref;
  if (ref != null && String(ref).trim() !== '') {
    stub.corporate_order_ref = String(ref);
  }
  return stub;
}

/**
 * @param {Array<{ id: number, corporate_order_ref?: string }>} stubs
 * @returns {Array<{ id: number, corporate_order_ref?: string }>}
 */
function dedupeRelatedOrderStubs(stubs) {
  const seen = new Set();
  const out = [];
  stubs.forEach(stub => {
    if (!stub || seen.has(stub.id)) return;
    seen.add(stub.id);
    out.push(stub);
  });
  return out;
}

/**
 * Map list rows or mixed id payloads to compact stubs.
 *
 * @param {*} rows
 * @returns {Array<{ id: number, corporate_order_ref?: string }>}
 */
export function mapBudgetOrderRowsToStubs(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return dedupeRelatedOrderStubs(
    list.map(toRelatedOrderStub).filter(Boolean)
  );
}

/**
 * Read compact related-order stubs from budget retrieve.
 *
 * Present when `related_orders`, `order_ids`, or `orders` is an array
 * (including empty). Missing those keys → `{ present: false }` so the
 * modal can fall back to `/orders/` when `order_count > 0`.
 *
 * @param {*} payload
 * @returns {{
 *   present: boolean,
 *   source: 'related_orders'|'order_ids'|'orders'|null,
 *   stubs: Array<{ id: number, corporate_order_ref?: string }>,
 * }}
 */
export function getBudgetRelatedOrderStubsFromRetrieve(payload) {
  if (!payload || typeof payload !== 'object') {
    return { present: false, source: null, stubs: [] };
  }
  if (Array.isArray(payload.related_orders)) {
    return {
      present: true,
      source: 'related_orders',
      stubs: mapBudgetOrderRowsToStubs(payload.related_orders),
    };
  }
  if (Array.isArray(payload.order_ids)) {
    return {
      present: true,
      source: 'order_ids',
      stubs: mapBudgetOrderRowsToStubs(payload.order_ids),
    };
  }
  if (Array.isArray(payload.orders)) {
    return {
      present: true,
      source: 'orders',
      stubs: mapBudgetOrderRowsToStubs(payload.orders),
    };
  }
  return { present: false, source: null, stubs: [] };
}

/**
 * GET /v1/budgets/{id}/
 *
 * Identity + `order_count`. Retrieve may include compact related-order
 * stubs (`related_orders` / `order_ids` / `orders`). Full list rows are
 * not required. See `38_BACKEND_HANDOFF_BUDGET_DETAIL_ORDERS.md`.
 *
 * @param {string} token
 * @param {string|number} id
 * @returns {Promise<{
 *   success: boolean,
 *   data?: Object,
 *   messages?: string[],
 *   status?: number,
 * }>}
 */
export const getBudgetLine = async (token, id) => {
  try {
    logInfo(LOG_CONTEXT, 'getBudgetLine start', { id });
    const response = await axios.get(`${BACKEND_URL}budgets/${id}/`, {
      headers: { Authorization: `Token ${token}` },
    });
    const payload =
      response.data && typeof response.data === 'object' ? response.data : {};
    const orderCount =
      typeof payload.order_count === 'number' ? payload.order_count : null;
    const stubs = getBudgetRelatedOrderStubsFromRetrieve(payload);
    logInfo(LOG_CONTEXT, 'getBudgetLine success', {
      id,
      orderCount,
      stubSource: stubs.source,
      stubCount: stubs.stubs.length,
    });
    BudgetCache.set(id, payload);
    return {
      success: true,
      data: payload,
    };
  } catch (error) {
    logError(LOG_CONTEXT, 'getBudgetLine failed', { id });
    return {
      success: false,
      messages: formatLabDataErrors(error.response?.data),
      status: error.response?.status,
    };
  }
};

/**
 * Retrieve with in-memory cache (Quote/Order lazy-detail pattern).
 * Hover/open should call this so a second open does not hit the network.
 *
 * @param {string} token
 * @param {string|number} id
 * @param {{ force?: boolean }} [options]
 * @returns {Promise<{
 *   success: boolean,
 *   data?: Object,
 *   cached?: boolean,
 *   messages?: string[],
 *   status?: number,
 * }>}
 */
export const getBudgetLineLazy = async (token, id, options = {}) => {
  const { force = false } = options;
  if (force) {
    BudgetCache.remove(id);
  } else {
    const cached = BudgetCache.get(id);
    if (cached) {
      logInfo(LOG_CONTEXT, 'getBudgetLineLazy cache hit', {
        id,
        orderCount:
          typeof cached.order_count === 'number' ? cached.order_count : null,
        stubSource: getBudgetRelatedOrderStubsFromRetrieve(cached).source,
      });
      return { success: true, data: cached, cached: true };
    }
  }
  const result = await getBudgetLine(token, id);
  return result.success ? { ...result, cached: false } : result;
};

/**
 * GET /v1/budgets/{id}/orders/ (one page). Follow DRF `next`.
 *
 * Procure detail modal source when retrieve has no compact stubs.
 * Preferred row: `{ id, corporate_order_ref }`. Until BE slims the
 * action, rows may still be `OrderListSerializer` — callers map via
 * `mapBudgetOrderRowsToStubs`. Empty budget: 200 + `results: []`.
 * Do not client-filter the global orders list.
 *
 * @param {string} token
 * @param {string|number} id
 * @param {{ nextPage?: string|null }} [options]
 * @returns {Promise<{
 *   success: boolean,
 *   data?: Array,
 *   nextPage?: string|null,
 *   reachedEnd?: boolean,
 *   messages?: string[],
 *   status?: number,
 * }>}
 */
export const getBudgetLineOrders = async (token, id, options = {}) => {
  const { nextPage = null } = options;
  const url = nextPage
    ? nextPage
    : `${BACKEND_URL}budgets/${id}/orders/?page_num=1`;

  try {
    logInfo(LOG_CONTEXT, 'getBudgetLineOrders start', {
      id,
      paginating: Boolean(nextPage),
    });
    const response = await axios.get(url, {
      headers: { Authorization: `Token ${token}` },
    });
    const { results, next } = parseBudgetOrdersPayload(response.data);
    logInfo(LOG_CONTEXT, 'getBudgetLineOrders success', {
      id,
      count: results.length,
      reachedEnd: !next,
    });
    return {
      success: true,
      data: results,
      nextPage: next || null,
      reachedEnd: !next,
    };
  } catch (error) {
    logError(LOG_CONTEXT, 'getBudgetLineOrders failed', {
      id,
      status: error.response?.status,
    });
    return {
      success: false,
      messages: formatLabDataErrors(error.response?.data),
      status: error.response?.status,
    };
  }
};

/**
 * One page of related-order stubs (`id` + optional `corporate_order_ref`).
 *
 * @param {string} token
 * @param {string|number} id
 * @param {{ nextPage?: string|null }} [options]
 * @returns {Promise<{
 *   success: boolean,
 *   data?: Array<{ id: number, corporate_order_ref?: string }>,
 *   nextPage?: string|null,
 *   reachedEnd?: boolean,
 *   messages?: string[],
 *   status?: number,
 * }>}
 */
export const getBudgetLineOrderStubsPage = async (token, id, options = {}) => {
  const result = await getBudgetLineOrders(token, id, options);
  if (!result.success) {
    return result;
  }
  const stubs = mapBudgetOrderRowsToStubs(result.data);
  logInfo(LOG_CONTEXT, 'getBudgetLineOrderStubsPage mapped', {
    id,
    count: stubs.length,
    reachedEnd: Boolean(result.reachedEnd),
  });
  return {
    ...result,
    data: stubs,
  };
};

/**
 * POST /v1/budgets/
 *
 * @param {string} token
 * @param {Object} payload
 */
export const createBudgetLine = async (token, payload) => {
  try {
    logInfo(LOG_CONTEXT, 'createBudgetLine start', {
      number: payload?.number ?? null,
      code: payload?.code ?? null,
    });
    const response = await axios.post(`${BACKEND_URL}budgets/`, payload, {
      headers: { Authorization: `Token ${token}` },
    });
    return {
      success: true,
      data: response.data,
      toast: () =>
        showToast('Budget created', 'success', 'top-right', 3000),
    };
  } catch (error) {
    logError(LOG_CONTEXT, 'createBudgetLine failed', error.response?.data ?? error);
    return {
      success: false,
      messages: formatLabDataErrors(error.response?.data),
    };
  }
};

/**
 * PATCH /v1/budgets/{id}/
 *
 * @param {string} token
 * @param {string|number} id
 * @param {Object} payload
 */
export const updateBudgetLine = async (token, id, payload) => {
  try {
    logInfo(LOG_CONTEXT, 'updateBudgetLine start', {
      id,
      number: payload?.number ?? null,
    });
    const response = await axios.patch(
      `${BACKEND_URL}budgets/${id}/`,
      payload,
      {
        headers: { Authorization: `Token ${token}` },
      }
    );
    const incoming =
      response.data && typeof response.data === 'object' ? response.data : {};
    const merged = mergeBudgetListRow(BudgetCache.get(id) || {}, incoming);
    BudgetCache.set(id, merged);
    logInfo(LOG_CONTEXT, 'updateBudgetLine success', {
      id,
      piId:
        merged.primary_investigator?.id ??
        merged.primary_investigator_id ??
        null,
      preservedOrderCount: typeof merged.order_count === 'number',
    });
    return {
      success: true,
      data: merged,
      toast: () =>
        showToast('Budget updated', 'success', 'top-right', 3000),
    };
  } catch (error) {
    logError(LOG_CONTEXT, 'updateBudgetLine failed', error.response?.data ?? error);
    return {
      success: false,
      messages: formatLabDataErrors(error.response?.data),
    };
  }
};

/**
 * DELETE /v1/budgets/{id}/
 *
 * Matches `DeleteButton`: `{ success, toast }` on success; array of error
 * strings on failure. Logs budget id only (no payload).
 *
 * Backend `BudgetViewSet` currently omits DELETE (`405`) until
 * `http_method_names` includes `"delete"`. See
 * `project-docs/36_BACKEND_HANDOFF_BUDGET_DELETE.md`.
 *
 * @param {string} token
 * @param {string|number} id
 * @returns {Promise<{ success: true, toast: Function }|string[]>}
 */
export const deleteBudgetLine = async (token, id) => {
  try {
    logInfo(LOG_CONTEXT, 'deleteBudgetLine start', { id });
    await axios.delete(`${BACKEND_URL}budgets/${id}/`, {
      headers: { Authorization: `Token ${token}` },
    });
    logInfo(LOG_CONTEXT, 'deleteBudgetLine success', { id });
    BudgetCache.remove(id);
    return {
      success: true,
      toast: () =>
        showToast('Budget deleted', 'success', 'top-right', 3000),
    };
  } catch (error) {
    logError(LOG_CONTEXT, 'deleteBudgetLine failed', { id });
    if (error.response?.status === 405) {
      return [
        'This server does not allow deleting budgets yet. Ask the backend team to enable DELETE on /v1/budgets/{id}/.',
      ];
    }
    const payload = error.response?.data;
    if (Array.isArray(payload)) {
      return payload.map(item => String(item));
    }
    return formatLabDataErrors(payload);
  }
};
